import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { Modal } from "@/components/Modal";
import { ProviderGlyph } from "@/components/ProviderGlyph";
import { RowSkeleton } from "@/components/Skeleton";
import { useToast } from "@/components/Toast";
import type { ProxyKey, ProxyKeyCreated, RealKey } from "@/types";

const REVEAL_AUTO_CLOSE_MS = 60_000;

export function ProxyKeys() {
  const [rows, setRows] = useState<ProxyKey[] | null>(null);
  const [reals, setReals] = useState<RealKey[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [issued, setIssued] = useState<ProxyKeyCreated | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const [secondsLeft, setSecondsLeft] = useState<number>(REVEAL_AUTO_CLOSE_MS / 1000);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    if (!issued) {
      setSecondsLeft(REVEAL_AUTO_CLOSE_MS / 1000);
      if (tickRef.current) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    setSecondsLeft(REVEAL_AUTO_CLOSE_MS / 1000);
    tickRef.current = window.setInterval(() => {
      setSecondsLeft((n) => {
        if (n <= 1) {
          setIssued(null);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [issued]);

  async function load() {
    try {
      const [p, r] = await Promise.all([
        api<ProxyKey[]>("/api/admin/proxy-keys"),
        api<RealKey[]>("/api/admin/real-keys"),
      ]);
      setRows(p);
      setReals(r);
    } catch (e: any) {
      setErr(e?.message ?? "load failed");
    }
  }
  useEffect(() => {
    load();
  }, []);

  // Deep-link support: command-palette "Issue proxy key" opens the modal via ?new=1
  useEffect(() => {
    if (params.get("new") === "1" && reals.length > 0) {
      setCreateOpen(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams, reals.length]);

  // `n` shortcut = open creation modal (when not typing in a form field)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const isForm =
        t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (!isForm && e.key === "n" && !e.metaKey && !e.ctrlKey && reals.length > 0) {
        e.preventDefault();
        setCreateOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [reals.length]);

  const realById = useMemo(() => {
    const m = new Map<number, RealKey>();
    reals.forEach((r) => m.set(r.id, r));
    return m;
  }, [reals]);

  const filtered = rows
    ? rows.filter((p) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          p.label.toLowerCase().includes(q) ||
          (p.project ?? "").toLowerCase().includes(q) ||
          p.prefix.toLowerCase().includes(q) ||
          (realById.get(p.real_key_id)?.provider ?? "").toLowerCase().includes(q)
        );
      })
    : null;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl display">Proxy keys</h1>
          <p className="text-sm text-slate-400">
            Keys your consumer projects embed. Full value is shown once at creation.
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setCreateOpen(true)}
          disabled={reals.length === 0}
          title={reals.length === 0 ? "Add a real key first" : ""}
        >
          + Issue proxy key
          <kbd className="ml-1 text-[10px] opacity-60">n</kbd>
        </button>
      </header>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <input
            type="search"
            className="input pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by label, project, prefix, provider…"
            aria-label="Search proxy keys"
          />
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">⌕</span>
          <kbd className="hidden sm:inline-block absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-600 mono">
            /
          </kbd>
        </div>
        <div className="text-xs text-slate-500">
          {rows === null ? "…" : `${filtered?.length ?? 0} of ${rows.length}`}
        </div>
      </div>

      {err && <div className="card text-rose-300 text-sm">{err}</div>}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500 uppercase tracking-wide bg-white/5">
            <tr>
              <th className="py-3 pl-4 pr-0 w-8">
                <input
                  type="checkbox"
                  aria-label="Select all active"
                  className="accent-accent-500"
                  checked={
                    !!filtered &&
                    filtered.filter((p) => !p.revoked_at).length > 0 &&
                    filtered.filter((p) => !p.revoked_at).every((p) => selected.has(p.id))
                  }
                  onChange={(e) => {
                    const next = new Set(selected);
                    filtered?.filter((p) => !p.revoked_at).forEach((p) => {
                      if (e.target.checked) next.add(p.id);
                      else next.delete(p.id);
                    });
                    setSelected(next);
                  }}
                />
              </th>
              <th className="py-3 pl-2">Provider</th>
              <th>Label</th>
              <th>Project</th>
              <th>Prefix</th>
              <th>Status</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <>
                <RowSkeleton cols={8} />
                <RowSkeleton cols={8} />
                <RowSkeleton cols={8} />
              </>
            )}
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 px-6 text-center text-slate-400">
                  <div className="text-lg mb-1">No proxy keys yet</div>
                  <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                    Issue a proxy key for a consumer project (Avon, SAM, Eva…). It's the
                    <span className="mono"> vlt_live_… </span>token you'll paste into that
                    project's env var — shown once at creation, never stored in plaintext.
                  </p>
                  <button
                    className="btn-primary"
                    onClick={() => setCreateOpen(true)}
                    disabled={reals.length === 0}
                  >
                    {reals.length === 0 ? "Add a real key first" : "Issue proxy key"}
                  </button>
                </td>
              </tr>
            )}
            {rows && rows.length > 0 && filtered?.length === 0 && (
              <tr>
                <td colSpan={8} className="py-8 px-6 text-center text-slate-500 text-sm">
                  No matches for "{query}".
                </td>
              </tr>
            )}
            {filtered?.map((p) => {
              const real = realById.get(p.real_key_id);
              const expired = p.expires_at && new Date(p.expires_at) < new Date();
              const edge = p.revoked_at
                ? "row-edge-dim"
                : expired
                ? "row-edge-err"
                : "row-edge-ok";
              const canSelect = !p.revoked_at;
              return (
                <tr
                  key={p.id}
                  className={`border-t border-white/5 hover:bg-white/[0.03] transition-colors ${edge}`}
                >
                  <td className="py-3 pl-4 pr-0 w-8">
                    {canSelect && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${p.label}`}
                        className="accent-accent-500"
                        checked={selected.has(p.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(p.id);
                          else next.delete(p.id);
                          setSelected(next);
                        }}
                      />
                    )}
                  </td>
                  <td className="py-3 pl-2">
                    <ProviderGlyph provider={real?.provider ?? "?"} />
                  </td>
                  <td className="font-medium">
                    <Link to={`/proxy-keys/${p.id}`} className="hover:text-accent-500">
                      {p.label}
                    </Link>
                  </td>
                  <td className="text-slate-400">{p.project ?? "—"}</td>
                  <td className="mono text-xs text-slate-500">{p.prefix}…</td>
                  <td>
                    {p.revoked_at ? (
                      <span className="badge-err">revoked</span>
                    ) : expired ? (
                      <span className="badge-err">expired</span>
                    ) : (
                      <span className="badge-ok">active</span>
                    )}
                  </td>
                  <td className="text-slate-400 whitespace-nowrap">
                    {p.expires_at
                      ? new Date(p.expires_at).toLocaleDateString()
                      : "never"}
                  </td>
                  <td className="text-right pr-4">
                    <Link
                      to={`/proxy-keys/${p.id}`}
                      className="text-accent-500 hover:underline text-xs"
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-ink-800/95 backdrop-blur-sm shadow-2xl">
          <span className="text-sm text-slate-200">
            {selected.size} proxy key{selected.size === 1 ? "" : "s"} selected
          </span>
          <button
            className="btn-secondary text-xs"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
          <button
            className="btn-danger text-xs"
            disabled={bulkBusy}
            onClick={async () => {
              if (!confirm(`Revoke ${selected.size} proxy key${selected.size === 1 ? "" : "s"}? Consumers will start getting 401 immediately.`)) return;
              setBulkBusy(true);
              const ids = Array.from(selected);
              let ok = 0;
              let fail = 0;
              for (const id of ids) {
                try {
                  await api(`/api/admin/proxy-keys/${id}/revoke`, { method: "POST" });
                  ok++;
                } catch {
                  fail++;
                }
              }
              setBulkBusy(false);
              setSelected(new Set());
              if (fail === 0) toast.success(`Revoked ${ok} proxy key${ok === 1 ? "" : "s"}`);
              else toast.error(`Revoked ${ok}, ${fail} failed`);
              await load();
            }}
          >
            {bulkBusy ? "Revoking…" : `Revoke ${selected.size}`}
          </button>
        </div>
      )}

      <Modal open={createOpen} title="Issue a new proxy key" onClose={() => setCreateOpen(false)}>
        <ProxyKeyForm
          reals={reals}
          onCreated={async (full) => {
            setCreateOpen(false);
            setIssued(full);
            toast.success("Proxy key issued");
            await load();
          }}
        />
      </Modal>
      <Modal open={!!issued} title="Copy your proxy key NOW" onClose={() => setIssued(null)}>
        {issued && (
          <div className="space-y-4">
            <p className="text-sm text-amber-300">
              This is the ONLY time the full key will be shown. Store it somewhere safe (your
              consumer project's env vars). If you lose it, revoke & re-issue.
              <br />
              <span className="text-xs text-slate-500">
                This modal auto-closes in {secondsLeft}s.
              </span>
            </p>
            <div className="bg-black/60 rounded-lg p-3 font-mono text-sm break-all select-all">
              {issued.full_value}
            </div>
            <div className="flex justify-end gap-2">
              <button
                className="btn-secondary"
                onClick={() => navigator.clipboard.writeText(issued.full_value)}
              >
                Copy
              </button>
              <button className="btn-primary" onClick={() => setIssued(null)}>
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function ProxyKeyForm({
  reals,
  onCreated,
  prefilledRealKeyId,
}: {
  reals: RealKey[];
  onCreated: (full: ProxyKeyCreated) => void;
  prefilledRealKeyId?: number;
}) {
  const [form, setForm] = useState({
    real_key_id: prefilledRealKeyId ?? (reals[0]?.id ?? 0),
    label: "",
    project: "",
    expires_at: "",
    request_cap_day: "",
    request_cap_month: "",
    allowed_path_prefixes: "",
    ip_allowlist: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  function upd<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const payload: any = {
        real_key_id: Number(form.real_key_id),
        label: form.label,
        project: form.project || null,
      };
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      if (form.request_cap_day) payload.request_cap_day = Number(form.request_cap_day);
      if (form.request_cap_month) payload.request_cap_month = Number(form.request_cap_month);
      if (form.allowed_path_prefixes.trim())
        payload.allowed_path_prefixes = form.allowed_path_prefixes
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      if (form.ip_allowlist.trim())
        payload.ip_allowlist = form.ip_allowlist
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      const created = await api<ProxyKeyCreated>("/api/admin/proxy-keys", {
        method: "POST",
        json: payload,
      });
      onCreated(created);
    } catch (e: any) {
      setErr(e?.message ?? "create failed");
    } finally {
      setLoading(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Bound real key">
        <select
          className="input"
          value={form.real_key_id}
          onChange={(e) => upd("real_key_id", Number(e.target.value) as any)}
          required
          disabled={!!prefilledRealKeyId}
        >
          {reals.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label} ({r.provider})
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Label">
          <input
            className="input"
            value={form.label}
            onChange={(e) => upd("label", e.target.value)}
            placeholder="avon-anthropic-prod"
            required
          />
        </Field>
        <Field label="Project (optional)">
          <input
            className="input"
            value={form.project}
            onChange={(e) => upd("project", e.target.value)}
            placeholder="avon"
          />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Expires at (optional)">
          <input
            className="input"
            type="datetime-local"
            value={form.expires_at}
            onChange={(e) => upd("expires_at", e.target.value)}
          />
        </Field>
        <Field label="Daily req cap (optional)">
          <input
            className="input"
            type="number"
            min={1}
            value={form.request_cap_day}
            onChange={(e) => upd("request_cap_day", e.target.value)}
          />
        </Field>
        <Field label="Monthly req cap (optional)">
          <input
            className="input"
            type="number"
            min={1}
            value={form.request_cap_month}
            onChange={(e) => upd("request_cap_month", e.target.value)}
          />
        </Field>
      </div>
      <Field label="Allowed path prefixes (comma-separated, optional)">
        <input
          className="input mono"
          value={form.allowed_path_prefixes}
          onChange={(e) => upd("allowed_path_prefixes", e.target.value)}
          placeholder="/v1/messages, /v1/complete"
        />
      </Field>
      <Field label="IP allowlist CIDRs (comma-separated, optional)">
        <input
          className="input mono"
          value={form.ip_allowlist}
          onChange={(e) => upd("ip_allowlist", e.target.value)}
          placeholder="10.0.0.0/24, 203.0.113.10/32"
        />
      </Field>
      {err && <div className="text-rose-300 text-xs">{err}</div>}
      <div className="flex justify-end">
        <button className="btn-primary" disabled={loading} type="submit">
          {loading ? "Issuing…" : "Issue proxy key"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}
